/**
 * Descripción declarativa del `<head>` de una página.
 *
 * Es **dato**, no JSX: lo devuelve el loader (server), se serializa junto al estado y el
 * cliente lo reaplica al navegar. Por eso no hace falta react-helmet: un solo mecanismo,
 * el mismo descriptor en servidor (`renderHead`) y en cliente (`useHead`).
 */
export interface HeadDescriptor {
  title: string;
  description?: string;
  /** URL absoluta y canónica de esta página (evita duplicados por query/UTM). */
  canonical?: string;
  /** Ej: 'index,follow' | 'noindex,nofollow'. Default del sitio: index,follow. */
  robots?: string;
  locale?: string;
  openGraph?: OpenGraph;
  twitter?: TwitterCard;
  /** Structured data (schema.org). Se emite un <script type="application/ld+json"> por item. */
  jsonLd?: JsonLd[];
}

export interface OpenGraph {
  /** 'website' para portada/listados, 'article' para notas. */
  type?: 'website' | 'article';
  title?: string;
  description?: string;
  url?: string;
  /** URL absoluta de la imagen social (1200x630 recomendado). */
  image?: string;
  imageAlt?: string;
  siteName?: string;
  locale?: string;
  /** Solo para type: 'article'. */
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  authors?: string[];
}

export interface TwitterCard {
  card?: 'summary' | 'summary_large_image';
  title?: string;
  description?: string;
  image?: string;
  site?: string;
}

/** Objeto JSON-LD (schema.org). Se tipa laxo a propósito: la forma la fijan los builders. */
export type JsonLd = Record<string, unknown>;

/**
 * Mergea un descriptor de página sobre el base del sitio (`defaultHead`).
 * Los campos anidados (openGraph/twitter) se mergean campo a campo.
 */
export function mergeHead(base: HeadDescriptor, page: Partial<HeadDescriptor>): HeadDescriptor {
  return {
    ...base,
    ...page,
    openGraph: { ...base.openGraph, ...page.openGraph },
    twitter: { ...base.twitter, ...page.twitter },
    ...(page.jsonLd ? { jsonLd: page.jsonLd } : {}),
  };
}
